import { ApiService } from '../../api/api-service';
import { Dealer } from '../../types';

const SERVICE_ROOT = '/dealer';

/**
 * Service for dealer-related API operations
 */
export class DealerService extends ApiService {
  /**
   * Get all dealers (no pagination)
   * @returns Promise resolving to all dealers
   */
  async getAllDealers(): Promise<Dealer[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}`),
      'Error fetching all dealers'
    );
  }

  /**
   * Get all dealers (no pagination)
   * @returns Promise resolving to all dealers
   */
  async getDealersByManufacturerId(manufacturerId: number): Promise<Dealer[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/manufacturer/${manufacturerId}`),
      'Error fetching all dealers'
    );
  }

  /**
   * Get a dealer by ID
   * @param id Dealer ID
   * @returns Promise resolving to the dealer data
   */
  async getDealerById(id: string): Promise<Dealer> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/${id}`),
      `Error fetching dealer with ID ${id}`
    );
  }

  /**
   * Create a new dealer
   * @param dealer Dealer data
   * @returns Promise resolving to the created dealer
   */
  async createDealer(dealer: Omit<Dealer, 'id'>): Promise<Dealer> {
    return this.handleApiCall(
      () => this.client.post(SERVICE_ROOT, dealer),
      'Error creating dealer'
    );
  }

  /**
   * Update a dealer
   * @param id Dealer ID
   * @param dealer Dealer data to update
   * @returns Promise resolving to the updated dealer
   */
  async updateDealer(id: string, dealer: Partial<Dealer>): Promise<Dealer> {
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/${id}`, dealer),
      `Error updating dealer with ID ${id}`
    );
  }

  /**
   * Delete a dealer
   * @param id Dealer ID
   * @returns Promise resolving when the dealer is deleted
   */
  async deleteDealer(id: string): Promise<void> {
    return this.handleApiCall(
      () => this.client.delete(`${SERVICE_ROOT}/${id}`),
      `Error deleting dealer with ID ${id}`
    );
  }

  /**
   * Get all document tiers
   * @returns Promise resolving to an array of document tier strings
   */
  async getDocumentTiers(): Promise<string[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/document-tiers`),
      'Error fetching document tiers'
    );
  }
}
